import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ExportButton } from '@/components/common/ExportButton';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchCadeActivity } from '@/services/cade';
import { RegisterPanels } from '@/pages/cade/RegisterPanels';
import { Stat } from '@/pages/cade/Stat';
import { WindowCaption } from '@/pages/cade/WindowCaption';
import type { CadeTimeToDecision, CadeUniverseSplit, CadeYearPoint } from '@/services/types';

/** The anchor the case list and the case page link to. */
export const TIME_TO_DECISION_ID = 'time-to-decision';

/**
 * CADE by year -- the first time dimension this corpus has had in the product.
 *
 * A separate page rather than a panel on the overview, and the separation is the
 * point. The overview counts 464 adjudicated tribunal cases; this counts 8,107
 * processes CADE itself publishes, and they overlap on 448. A year chart sitting
 * on the overview would invite a reader to carry that page's denominator onto
 * this one, which is the same two-populations error the case registry already
 * forced once.
 *
 * The archive line on the chart is not decoration. It shows how much of each year
 * the rest of the CADE screens can actually describe -- and for 2016 and 2017
 * the answer is nothing at all.
 */
export function CadeActivityPage() {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'activity'],
    queryFn: fetchCadeActivity,
  });

  // A link from the case list or a case page arrives with a hash. The browser
  // scrolls to a hash only for an element present at load, and this section
  // renders after the query resolves -- so scroll once the data is here.
  const { hash } = useLocation();
  useEffect(() => {
    if (!data || hash !== `#${TIME_TO_DECISION_ID}`) return;
    document.getElementById(TIME_TO_DECISION_ID)?.scrollIntoView({ block: 'start' });
  }, [data, hash]);

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:activity.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:activity.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cade:activity.explainer" />

      <AsOfStrip asOf={data.as_of} />

      {/* The population disclosure leads the page. Everything below counts a
          different corpus from the rest of the CADE screens, and a reader who
          misses that will read every number here as the tribunal's. */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:activity.universe.title')}
        </h2>
        <Answer answer={data.universe} help={t('cade:activity.universe.help')}>
          {(split) => <UniversePanel split={split} />}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:activity.guide.universe" />
        </div>
      </section>

      <div className="flex justify-end">
        <ExportButton path="/api/v1/cade/export/activity" />
      </div>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:activity.trend.title')}
        </h2>
        <Answer answer={data.trend} help={t('cade:activity.trend.help')}>
          {(points) => <ActivityChart points={points} />}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:activity.guide.chart" />
        </div>
      </section>

      {/* The fine series, refused. Given its own card rather than omitted: "this
          cannot be drawn and here is the concentration" is a finding; a missing
          line is not. */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cade:activity.fines.title')}
        </h2>
        {/* `elsewhere`, not an empty state. What is refused here is the LINE;
            the yearly totals themselves are real and are printed in the table
            below beside the count each rests on. Without this the card read
            "nothing to show" directly above a guide explaining where to read
            them. */}
        <Answer answer={data.fines} elsewhere={t('cade:activity.fines.elsewhere')}>
          {() => null}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:activity.guide.fines" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:activity.table.title')}
        </h2>
        {/* NOT a second <Answer>. `trend` is already rendered as one above, so
            passing it to two of them printed the substrate badge and the reason
            twice on a refusal -- and this one carried no `help`, so the two
            copies disagreed on their footer. The refusal belongs to the chart
            card; this table simply has nothing to draw when there is no value. */}
        {data.trend.value ? (
          <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('cade:activity.table.year')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('cade:activity.table.processes')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('cade:activity.table.withFine')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('cade:activity.table.fines')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('cade:activity.table.inArchive')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.trend.value.map((point) => (
                    <tr key={point.year ?? 'undated'} className="border-t hover:bg-muted/30">
                      <td className="px-3 py-2 tabular-nums text-foreground">
                        {point.year ?? (
                          <span className="italic text-muted-foreground">
                            {t('cade:activity.undated')}
                          </span>
                        )}
                        {point.partial ? (
                          <span className="ml-2 rounded border px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                            {t('cade:activity.partialYear')}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(point.processes)}
                      </td>
                      {/* The N that makes the money column readable. It sits
                          immediately left of the amount on purpose. */}
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(point.with_fine)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {point.fines_brl > 0 ? currency.format(point.fines_brl) : ''}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums ${
                          point.in_archive === 0 ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'
                        }`}
                      >
                        {number.format(point.in_archive)}
                      </td>
                    </tr>
                  ))}
                </tbody>
            </table>
          </div>
        ) : null}
        <div className="mt-3">
          <FieldGuide i18nKey="cade:activity.guide.columns" />
        </div>
      </section>

      {/* The figure every client asks first. Its own card, above the case-type
          mix, because the mix says how many matters of a type there are and
          this says how long they took -- and only the register can say it:
          the archive has no parsed date. */}
      <section id={TIME_TO_DECISION_ID} className="rounded-lg border bg-card p-4">
        <h2 className="mb-1 text-sm font-semibold text-foreground">
          {t('cade:activity.duration.title')}
        </h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('cade:activity.duration.intro')}</p>
        <Answer answer={data.time_to_decision} help={t('cade:activity.duration.help')}>
          {(ladder) => <DurationLadderTable ladder={ladder} />}
        </Answer>
        <div className="mt-2">
          <WindowCaption window={data.as_of.year_window} />
        </div>
        <div className="mt-3 flex justify-end">
          <ExportButton path="/api/v1/cade/export/activity/duration" />
        </div>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:activity.guide.duration" />
        </div>
      </section>

      {/* The register read at protocol grain: durations to the day, the SG's
          decisions and what the Tribunal later did with them, appeals, TCC
          contributions and sectors. Same population as this page, never the
          archive's. */}
      <RegisterPanels />

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:activity.caseTypes.title')}
        </h2>
        <ul className="space-y-1.5 text-sm">
          {data.case_types.map((type) => (
            <li key={type.case_type} className="flex items-baseline justify-between gap-4">
              <span className="truncate text-foreground">{type.case_type}</span>
              <span className="shrink-0 text-right tabular-nums text-muted-foreground">
                {number.format(type.processes)}
                {/* The register's own amount field, summed per type and shown
                    with its N because it is a statement about a fraction of
                    the row: measured 2026-09-14, only administrative
                    proceedings carry any, on 204 of 321. Never a trend and
                    never totalled across types -- the refusal above says why. */}
                {type.with_fine > 0 ? (
                  <span className="block text-xs">
                    {t('cade:activity.caseTypes.withFine', {
                      sum: currency.format(type.fines_brl),
                      count: type.with_fine,
                    })}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
        {/* The denominator names this page's source: the published "CADE em
            Numeros" register, not the tribunal archive. The same label carries
            a slightly different count on the overview, which counts `cases`
            rows -- two populations, and this line is what tells them apart. */}
        {data.universe.value ? (
          <p className="mt-3 text-xs text-muted-foreground">
            {t('cade:activity.caseTypes.denominator', {
              processes: number.format(data.universe.value.qlik_processes),
            })}
          </p>
        ) : null}
        <p className="mt-3 text-xs text-muted-foreground">{t('cade:activity.caseTypes.noOutcome')}</p>
      </section>
    </div>
  );
}

/**
 * One row per case type: n, the quartile ladder in whole years, the maximum,
 * and the share decided in the filing year. A type below the floor keeps its
 * row with its count and a stated reason, so a reader looking for it finds it
 * rather than concluding the register has none.
 */
function DurationLadderTable({ ladder }: { ladder: CadeTimeToDecision }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  // Whole-year quantiles interpolate between years; one decimal is the most a
  // reader should be shown on a year scale.
  const years = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 });

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground" data-time-to-decision>
        {t('cade:activity.duration.reach', {
          decided: number.format(ladder.decided),
          first: ladder.first_decision_year ?? '—',
          last: ladder.last_decision_year ?? '—',
          undated: number.format(ladder.undated),
        })}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">{t('cade:activity.duration.columns.type')}</th>
              <th className="px-3 py-2 text-right font-medium">
                {t('cade:activity.duration.columns.decided')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('cade:activity.duration.columns.inArchive')}
              </th>
              <th className="px-3 py-2 text-right font-medium">p25</th>
              <th className="px-3 py-2 text-right font-medium">
                {t('cade:activity.duration.columns.median')}
              </th>
              <th className="px-3 py-2 text-right font-medium">p75</th>
              <th className="px-3 py-2 text-right font-medium">p90</th>
              <th className="px-3 py-2 text-right font-medium">
                {t('cade:activity.duration.columns.max')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('cade:activity.duration.columns.sameYear')}
              </th>
            </tr>
          </thead>
          <tbody>
            {ladder.ladders.map((row) => (
              <tr key={row.case_type} className="border-t hover:bg-muted/30">
                <td className="px-3 py-2 text-foreground">{row.case_type}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {number.format(row.decided)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {number.format(row.in_archive)}
                </td>
                {row.below_floor ? (
                  <td colSpan={6} className="px-3 py-2 text-xs italic text-muted-foreground">
                    {t('cade:activity.duration.belowFloor')}
                  </td>
                ) : (
                  <>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {years.format(row.p25 ?? 0)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-foreground">
                      {t('cade:activity.duration.years', { count: row.median ?? 0, value: years.format(row.median ?? 0) })}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {years.format(row.p75 ?? 0)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {years.format(row.p90 ?? 0)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.maximum ?? 0)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {percent.format(row.share_same_year ?? 0)}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UniversePanel({ split }: { split: CadeUniverseSplit }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-3 text-xs">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={t('cade:activity.universe.published')}
          value={number.format(split.qlik_processes)}
        />
        <Stat
          label={t('cade:activity.universe.inArchive')}
          value={number.format(split.in_archive)}
        />
        <Stat
          label={t('cade:activity.universe.outside')}
          value={number.format(split.outside_archive)}
        />
        <Stat
          label={t('cade:activity.universe.tribunal')}
          value={number.format(split.tribunal_cases)}
        />
      </dl>
      {split.years_without_archive.length > 0 ? (
        <p className="flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/5 p-2 text-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
          <span>
            {t('cade:activity.universe.barrenYears', {
              years: split.years_without_archive.join(', '),
            })}
          </span>
        </p>
      ) : null}
    </div>
  );
}


/**
 * Processes per year as bars, with tribunal-archive coverage as a line on its
 * own axis.
 *
 * Two axes because the scales differ by an order of magnitude (487-976 against
 * 0-100). Sharing one would flatten the archive line onto the baseline and hide
 * the two years where it touches zero, which is the single most important thing
 * this chart has to show.
 */
function ActivityChart({ points }: { points: CadeYearPoint[] }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  // The undated bucket is real and is NOT a period, so it stays out of a chart
  // whose x-axis is time. It remains in the table below, where it cannot be
  // mistaken for a year.
  const data = points
    .filter((point) => point.year !== null)
    .map((point) => ({
      year: point.year,
      processes: point.processes,
      inArchive: point.in_archive,
    }));

  return (
    <div className="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis dataKey="year" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
          <YAxis
            yAxisId="processes"
            tickFormatter={(v: number) => number.format(v)}
            tick={{ fontSize: 11 }}
            width={52}
            className="fill-muted-foreground"
          />
          <YAxis
            yAxisId="archive"
            orientation="right"
            tickFormatter={(v: number) => number.format(v)}
            tick={{ fontSize: 11 }}
            width={44}
            className="fill-muted-foreground"
          />
          <Tooltip
            formatter={(value: number) => number.format(value)}
            contentStyle={{
              background: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '0.25rem',
              fontSize: '0.75rem',
            }}
          />
          <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
          <Bar
            yAxisId="processes"
            dataKey="processes"
            name={t('cade:activity.chart.processes')}
            fill="hsl(var(--muted-foreground))"
            fillOpacity={0.25}
            radius={[2, 2, 0, 0]}
          />
          <Line
            yAxisId="archive"
            type="monotone"
            dataKey="inArchive"
            name={t('cade:activity.chart.inArchive')}
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            dot={{ r: 2 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
